'use client'

import styles from './SalesInfo.module.css'
import { useEffect, useState } from 'react'

import { CatalogGridLoader } from '@/screens/catalog/ui/catalog-grid/catalog-grid-loader/catalog-grid-loader'
import { SaleSortType, SortTypeSelector } from '@/screens/CatalogSlug/page/ui/SortPanel/SortTypeSelector'

import { CatalogGrid } from '@/entities/catalog'
import { promotionCategoriesAdapter } from '@/entities/categories'
import { fetchPromotionProducts } from '@/entities/sales'

import { BASE_URL } from '@/shared/lib/const/env'
import { useMediaQueries } from '@/shared/lib/hooks/useMediaQueries'
import { TPromotionCategories } from '@/shared/types/deprecated-types'
import { TProduct } from '@/shared/types/product-types'
import { ExpandableRadioGroup, PaginationNew } from '@/shared/ui'
import { Col, Row } from '@/shared/ui/Grid'
import { Image } from '@/shared/ui/Image'
import { ItemLoader } from '@/shared/ui/Skeletons/variants/ItemLoader/ItemLoader'

import { PromotionBottomDescription } from './ui/PromotionBottomDescription/PromotionBottomDescription'
import { PromotionTerms } from './ui/PromotionTerms/PromotionTerms'

export type TPromotion = {
	NAME: string
	CODE: string
	DETAIL_TEXT: string
	DETAIL_PICTURE: string
	TERMS_PROMOTION?: string
	TITLE_TERMS_PROMOTION?: string
	ACTIVE_TO: string
	ACTIVE: string
	categories?: TPromotionCategories
	promotionsItems: TProduct[]
	lastPage: number
	currentPage: number
	total: number
	initialCategory?: string
	initialOrder?: string
	initialLimit?: string
}

const allProductsVariant = {
	value: `all`,
	label: `Все товары`
}

const EMPTY_ITEMS: TProduct[] = []

export const SalesInfo = (props: TPromotion): JSX.Element => {
	const {
		NAME,
		categories,
		DETAIL_PICTURE,
		CODE,
		ACTIVE_TO,
		DETAIL_TEXT,
		promotionsItems = EMPTY_ITEMS,
		TERMS_PROMOTION,
		TITLE_TERMS_PROMOTION,
		lastPage,
		currentPage,
		total,
		initialCategory,
		initialOrder,
		initialLimit
	} = props
	const categoriesList = categories?.length ? promotionCategoriesAdapter(categories) : null
	const [isLoading, setIsLoading] = useState<boolean>(false)
	const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false)
	const limit = initialLimit || '32'

	const [products, setProducts] = useState(promotionsItems)
	const [pagination, setPagination] = useState({ currentPage, lastPage })
	const [activeCategory, setActiveCategory] = useState(initialCategory || 'all')
	const [activeOrder, setActiveOrder] = useState(initialOrder || SaleSortType.PopularityToLow)
	const [totalProducts, setTotalProducts] = useState(total)
	const { isMobile } = useMediaQueries()

	useEffect(() => {
		setProducts(promotionsItems)
		setPagination({ currentPage, lastPage })
		setActiveCategory(initialCategory || 'all')
		setActiveOrder(initialOrder || SaleSortType.PopularityToLow)
		setTotalProducts(total)
	}, [currentPage, lastPage, promotionsItems, total, initialCategory, initialOrder])

	const loadProductsByFilters = async (nextCategory: string, nextOrder: string, page = 1) => {
		setIsLoading(true)
		try {
			const res = await fetchPromotionProducts(CODE, '', page, nextCategory, nextOrder, limit)

			if (res) {
				const { ITEMS, CURRENT_PAGE, LAST_PAGE, TOTAL } = res
				setProducts(ITEMS)
				setPagination({ currentPage: CURRENT_PAGE, lastPage: LAST_PAGE })
				setTotalProducts(TOTAL)
				return
			}

			setProducts([])
			setPagination({ currentPage: 1, lastPage: 1 })
			setTotalProducts(0)
		} finally {
			setIsLoading(false)
		}
	}

	const onCategoryChange = (nextCategory: string): void => {
		setActiveCategory(nextCategory)
		void loadProductsByFilters(nextCategory, activeOrder, 1)
	}
	const onSortChange = (sort: string): void => {
		setActiveOrder(sort)
		void loadProductsByFilters(activeCategory, sort, 1)
	}
	const onPageChange = (page: number): void => {
		void loadProductsByFilters(activeCategory, activeOrder, page)
	}
	const onShowMoreClick = (): void => {
		if (isLoadingMore) return
		setIsLoadingMore(true)

		void (async () => {
			try {
				const res = await fetchPromotionProducts(
					CODE,
					'',
					pagination.currentPage + 1,
					activeCategory,
					activeOrder,
					limit
				)

				if (res) {
					const { ITEMS, CURRENT_PAGE, LAST_PAGE, TOTAL } = res
					setProducts(prev => [...prev, ...ITEMS])
					setPagination({ currentPage: CURRENT_PAGE, lastPage: LAST_PAGE })
					setTotalProducts(TOTAL)
				}
			} finally {
				setIsLoadingMore(false)
			}
		})()
	}
	return (
		<section className={styles.promotionInfo}>
			{/* Title */}
			<h1 className={styles.promotionInfoTitle}>{NAME}</h1>

			{!!DETAIL_PICTURE && (
				<div className={styles.promotionInfoBanner}>
					<div style={{ position: 'relative', width: '100%', aspectRatio: '1440 / 552' }}>
						<Image alt={NAME} src={BASE_URL + DETAIL_PICTURE} fill style={{ objectFit: 'cover' }} />
					</div>
				</div>
			)}

			{/* Detailed promotion info */}
			<div className={styles.promotionInfoContent}>
				<Row justify="space-between">
					{/* Terms and steps of the promotion */}
					<Col span={24} className={styles.content}>
						<PromotionTerms ACTIVE_TO={ACTIVE_TO} description={DETAIL_TEXT} />
					</Col>
					<Col xl={6} xs={0}></Col>
					{/* Main info about action*/}
					{categoriesList && (
						<Col xl={24} xs={24}>
							<div className={styles.promotionInfoButtons}>
								<ExpandableRadioGroup
									items={[allProductsVariant, ...categoriesList]}
									value={activeCategory}
									changeHandler={onCategoryChange}
									showMobileScrollBar={true}
								/>
							</div>
						</Col>
					)}

					{!!promotionsItems?.length && (
						<>
							<Col span={24}>
								<SortTypeSelector
									isSaleSort
									onChange={onSortChange}
									value={activeOrder as SaleSortType}
									defaultValue={activeOrder as SaleSortType}
									className={styles.sortTypeSelectorSales}
								/>
							</Col>
						</>
					)}
					<Col span={24}>
						{isLoading ? (
							<CatalogGridLoader isMobile={isMobile} />
						) : (
							<CatalogGrid items={products} isLoading={isLoading} />
						)}
						{isLoadingMore && products?.map(item => <ItemLoader key={item.ID} />)}
						{pagination.lastPage > 1 && totalProducts > products.length && (
							<PaginationNew
								lastPage={pagination.lastPage}
								currentPage={pagination.currentPage}
								onChange={onPageChange}
								onMoreClick={onShowMoreClick}
								loading={isLoadingMore}
								showMoreButton
								withUrlParam={false}
								shallow={false}
							/>
						)}
					</Col>

					{TERMS_PROMOTION && TITLE_TERMS_PROMOTION && (
						<Col span={24}>
							<PromotionBottomDescription description={TERMS_PROMOTION} title={TITLE_TERMS_PROMOTION} />
						</Col>
					)}
				</Row>
			</div>
		</section>
	)
}
